import React, { useRef, useEffect } from 'react';
import { RootState } from 'src/store/reducers/state';
import { useSelector, useDispatch } from 'react-redux';
import { useUserActions } from 'src/store/users/actions';
type MemberSummary = {
    firstName?: string;
    lastName?: string;
    email?: string;
    profileURL?: string;
};

type Props = {
    member: MemberSummary;
    adminName?: string;
    isGenerating: boolean;
    onGenerate: () => void;
};

const ImpersonateWarningModal: React.FC<Props> = ({
    member,
    adminName,
    isGenerating,
    onGenerate
}) => {
    const btnRef = useRef<HTMLButtonElement>(null);
    const hiddenBtnRef = useRef<HTMLButtonElement>(null);
    const dispatch = useDispatch();
    const userAction = useUserActions(dispatch);
    const {
        isMemberTokenGenerated,
    } = useSelector((state: RootState) => ({
        isMemberTokenGenerated: state.users.isMemberTokenGenerated,
    }));

    useEffect(() => {
        if (!isMemberTokenGenerated) return;
        const modal = document.getElementById('ImpersonateWarningModal');
        const handleModalHidden = () => {
            hiddenBtnRef.current?.click();
            userAction.resetMemberRequest()
        };
        modal?.addEventListener('hidden.bs.modal', handleModalHidden);
        btnRef.current?.click();
        return () => {
            modal?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [isMemberTokenGenerated]);
    return (
        <div
            className={`modal fade fixed top-0 left-0 hidden w-full h-full outline-none overflow-x-hidden overflow-y-auto modal-open`}
            id="ImpersonateWarningModal"
            aria-labelledby="ImpersonateWarningModalLabel"
            aria-hidden="true">
            <div className="modal-dialog relative w-auto pointer-events-none max-w-xl">
                <div className="modal-content border-none shadow-lg relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current">
                    <div className="relative modal-header flex flex-shrink-0 items-center justify-between p-4 pb-2 rounded-t-md">
                        <button
                            ref={btnRef}
                            type="button"
                            className="dismiss-btn"
                            data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>

                    <div className="modal-body px-4 pb-4">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="theme-light-clr site-clr h-9 w-9 flex items-center justify-center rounded-full">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </span>
                            <div>
                                <h3 className="font-semibold text-lg">Impersonate Member</h3>
                                <p className="text-sm text-gray-500">
                                    Generate a temporary login link to access Webcastcloud as this member.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 bg-gray-100 rounded-md p-3 mb-4">
                            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                                <img
                                    className="w-full h-full object-cover"
                                    src={member.profileURL || '../../assets/img/user-png.png'}
                                />
                            </div>
                            <div>
                                <p className="font-semibold text-sm">
                                    {member.firstName} {member.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{member.email}</p>
                            </div>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 bg-yellow-50">
                            <p className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                <i className="fas fa-exclamation-triangle text-yellow-500"></i> Please note
                            </p>
                            <ul className="text-xs text-amber-800 space-y-1 list-disc pl-4">
                                <li>{`Access using this link may be recorded in the member's account activity or audit history.`}</li>
                                <li>The member may be able to see that their account has been accessed, depending on the activity and features used.</li>
                                <li>{`This will not change the member's Member Type, Member Lists, role or account configuration.`}</li>
                                <li>Any actions performed after logging in may be recorded against the member, including content activity, registrations, purchases or course progress.</li>
                            </ul>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-xs text-blue-800">
                            <i className="fas fa-info-circle mt-0.5 mr-1"></i>
                            This impersonation will be logged as performed by your admin account ({adminName || 'Admin'}).
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                data-bs-dismiss="modal"
                                className="mx-0 px-6 btn-primary discard-btn w-28 mt-0"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={isGenerating}
                                onClick={onGenerate}
                                className="px-6 btn-primary w-64 mr-2 ml-0 mt-0">
                                {isGenerating ? 'Generating...' : 'Generate Login Link'}
                            </button>
                            <button
                                ref={hiddenBtnRef}
                                id="hiddenBtn"
                                type="button"
                                className="hidden"
                                data-bs-toggle="modal"
                                data-bs-target="#ImpersonateLinkModal"
                            >
                                Hidden
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImpersonateWarningModal;